/**
 * Player-versus-entity and player-versus-object resolution.
 *
 * This is the single implementation of the interaction pass that used to sit
 * privately in `App.collideEntities` / `App.objectRect` in `main.ts`. It was
 * extracted so a programmatic playtest can run the *same* rules a human plays
 * under, rather than a second copy of the stomp and pickup logic that could
 * drift from the real one.
 *
 * It is deliberately shell-free. The function takes a plain `Level` and
 * `Player` and returns a plain contact descriptor instead of writing into the
 * shell's `deathContact` field, so `main.ts` stays the only place that maps a
 * contact onto the diagnostics classifier, and the module keeps its `game/`
 * boundary (no editor, renderer, DOM or `core/` imports).
 */

import { TILE } from '../engine/constants'
import { HARMLESS_KINDS } from './entities'
import type { Level, PlacedObject } from './level'
import { rectsOverlap, type Player } from './player'

/**
 * Something that touched the player and could later explain a death.
 *
 * Structurally the non-null half of `DeathContact` in `core/diagnostics.ts`,
 * duplicated here as a plain descriptor so `game/` does not depend on `core/`.
 */
export interface Contact {
  kind: 'enemy' | 'object'
  id: string
}

export interface InteractionOptions {
  /** `godMode`: lethal contact cannot hurt. Mirrors the shell's debug flag. */
  god?: boolean
}

/**
 * Resolve this frame's player interactions.
 *
 * Call once per simulation step, after `level.step()`: the level has moved its
 * entities and objects, and the shell is about to look at the player's state.
 * Returns the first hostile contact seen this frame (an enemy or a lethal
 * object), or null. The caller decides what to do with it; the pass itself
 * never logs.
 */
export function resolveInteractions(
  level: Level,
  player: Player,
  opts: InteractionOptions = {},
): Contact | null {
  if (level.status !== 'playing' || !player.alive) return null
  const god = opts.god === true
  const pr = player.rect
  let contact: Contact | null = null

  for (const e of level.entities) {
    if (!player.alive) break
    if (!e.alive) continue
    // Effects and remains are not combatants: walking over the flattened
    // sprite a stomp left behind must not hurt anybody. See `HARMLESS_KINDS`.
    if (HARMLESS_KINDS.has(e.kind)) continue
    if (!rectsOverlap(pr, e)) continue

    const stomping = player.vy > 0 && pr.y + pr.h - e.y < e.h * 0.7

    if (
      e.kind === 'coin' ||
      e.kind === 'expansion-cell' ||
      e.kind === 'ember-cell' ||
      e.kind === 'prism' ||
      e.kind === 'spare-battery'
    ) {
      e.onTouch?.(level, player)
      continue
    }

    if (stomping && e.onStomp !== undefined) {
      const consumed = e.onStomp(level, player)
      if (consumed) {
        player.bounce()
        continue
      }
    }

    // Note the contact *before* dispatching, not in the fallback branch.
    // Almost every enemy implements `onTouch` and calls `player.hurt()`
    // itself, so a note placed after the `onTouch` branch would never be
    // reached for any of them -- and every enemy death would be logged as
    // `unknown`, which is precisely the ambiguity this log exists to remove.
    contact ??= { kind: 'enemy', id: e.kind }

    if (e.onTouch !== undefined) {
      if (god) continue // `godMode`: lethal contact cannot hurt
      e.onTouch(level, player)
    } else if (player.isShielded) e.alive = false
    else if (!god) player.hurt()
  }

  // Placed pickups and lethal objects.
  for (const o of level.objects) {
    if (!player.alive) break
    if (!o.alive) continue
    const def = o.def
    if (
      def.lethal !== true &&
      def.pickup !== true &&
      def.id !== 'switch' &&
      def.id !== 'vine' &&
      def.id !== 'checkpoint'
    ) {
      continue
    }
    if (o.state.collected === true) continue
    const hit = objectRect(o)
    if (!rectsOverlap(pr, hit)) continue

    if (def.pickup === true && o.behaviour !== undefined) {
      o.behaviour.onSideHit?.({
        tick: level.tick,
        level,
        player,
        state: o.state,
      })
      continue
    }
    if (def.lethal === true && player.isShielded !== true) {
      contact ??= { kind: 'object', id: def.id }
      if (!god) player.kill()
      continue
    }
    o.behaviour?.onSideHit?.({
      tick: level.tick,
      level,
      player,
      state: o.state,
    })
  }

  return contact
}

/** Pixel rect of a placed object, honouring its hitbox inset. */
export function objectRect(o: PlacedObject): {
  x: number
  y: number
  w: number
  h: number
} {
  const hb = o.def.hitbox
  const baseX = o.tx * TILE + o.ox
  const baseY = o.ty * TILE + o.oy
  if (hb === undefined) {
    return { x: baseX, y: baseY, w: o.def.w * TILE, h: o.def.h * TILE }
  }
  return {
    x: baseX + (hb.ox ?? 0),
    y: baseY + (hb.oy ?? 0),
    w: hb.w,
    h: hb.h,
  }
}
